import * as Yup from "yup";

export const loginValidationSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required")
    .trim(),
  password: Yup.string().required("Password is required").trim(),
});

export const emailSendValidationSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required")
    .trim(),
});

export const passwordValidationSchema = Yup.object({
  otp: Yup.string().required("Otp is required").trim(),
  password: Yup.string().required("Password is required").trim(),
  repassword: Yup.string().required("Re-Password is required").trim(),
});

export const MAX_OPTION_LENGTH = 80;
export const OPTION_STAT_LABEL_MAX_LENGTH = 50;

export const predictionValidationSchema = Yup.object().shape({
  question: Yup.string()
    .required("Question is required")
    .trim()
    .min(5, "Question must be at least 5 characters"),
  subtitle: Yup.string()
    .required("Subtitle is required")
    .trim()
    .min(3, "Subtitle must be at least 3 characters"),
  tags: Yup.string()
    .required("At least one tag is required")
    .trim(),
  end_date: Yup.date()
    .required("End date is required")
    .min(new Date(), "End date must be in the future"),
  options: Yup.array()
    .of(
      Yup.string()
        .trim()
        .required("Option cannot be empty")
        .max(
          MAX_OPTION_LENGTH,
          `Option must be at most ${MAX_OPTION_LENGTH} characters`
        )
    )
    .min(2, "At least 2 options are required")
    .max(5, "Maximum 5 options allowed")
    .test("unique-options", "Duplicate options are not allowed", (options) => {
      if (!options) return true;
      const trimmed = options.map((o) => o?.trim().toLowerCase()).filter(Boolean);
      return new Set(trimmed).size === trimmed.length;
    }),
});

export const MIN_TOURNAMENT_QUESTIONS = 1;
export const MAX_TOURNAMENT_QUESTIONS = 20;
export const MAX_TOURNAMENT_OPTIONS = 5;

export const tournamentGenerateValidationSchema = Yup.object().shape({
  title: Yup.string()
    .required("Title is required")
    .trim()
    .min(3, "Title must be at least 3 characters"),
  description: Yup.string()
    .required("Description is required")
    .trim()
    .min(10, "Description must be at least 10 characters"),
  ai_instruction: Yup.string().trim(),
  number_of_questions: Yup.number()
    .required("Number of questions is required")
    .min(
      MIN_TOURNAMENT_QUESTIONS,
      `Minimum ${MIN_TOURNAMENT_QUESTIONS} question`
    )
    .max(
      MAX_TOURNAMENT_QUESTIONS,
      `Maximum ${MAX_TOURNAMENT_QUESTIONS} questions`
    ),
  start_date: Yup.date()
    .required("Start date is required")
    .typeError("Start date is required"),
  end_date: Yup.date()
    .required("End date is required")
    .typeError("End date is required")
    .min(Yup.ref("start_date"), "End date must be after start date"),
});

export const TOURNAMENT_END_DATE_SHORTEN_ERROR =
  "End date cannot be earlier than the current end date once the tournament has already started";

export const hasTournamentStarted = (startDate, now = new Date()) => {
  if (!startDate) return false;
  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) return false;
  return now.getTime() >= start.getTime();
};

export const getTournamentEditValidationSchema = ({
  originalStartDate,
  originalEndDate,
} = {}) =>
  Yup.object().shape({
    title: Yup.string()
      .required("Title is required")
      .trim()
      .min(3, "Title must be at least 3 characters"),
    description: Yup.string()
      .required("Description is required")
      .trim()
      .min(10, "Description must be at least 10 characters"),
    start_date: Yup.date()
      .required("Start date is required")
      .typeError("Start date is required"),
    end_date: Yup.date()
      .required("End date is required")
      .typeError("End date is required")
      .min(Yup.ref("start_date"), "End date must be after start date")
      .test(
        "cannot-shorten-started-tournament",
        TOURNAMENT_END_DATE_SHORTEN_ERROR,
        function (value) {
          if (!value || !originalEndDate) return true;
          if (!hasTournamentStarted(originalStartDate)) return true;
          return new Date(value).getTime() >= new Date(originalEndDate).getTime();
        }
      ),
    questions: Yup.array()
      .of(
        Yup.object().shape({
          question: Yup.string()
            .required("Question is required")
            .trim()
            .min(5, "Question must be at least 5 characters"),
          options: Yup.array()
            .of(
              Yup.string()
                .trim()
                .required("Option cannot be empty")
                .max(
                  MAX_OPTION_LENGTH,
                  `Option must be at most ${MAX_OPTION_LENGTH} characters`
                )
            )
            .min(2, "At least 2 options are required")
            .max(MAX_TOURNAMENT_OPTIONS, "Maximum 5 options allowed")
            .test(
              "unique-options",
              "Duplicate options are not allowed",
              (options) => {
                if (!options) return true;
                const trimmed = options
                  .map((o) => o?.trim().toLowerCase())
                  .filter(Boolean);
                return new Set(trimmed).size === trimmed.length;
              }
            ),
        })
      )
      .min(1, "At least one question is required")
      .max(
        MAX_TOURNAMENT_QUESTIONS,
        `Maximum ${MAX_TOURNAMENT_QUESTIONS} questions allowed`
      ),
  });

export const tournamentEditValidationSchema = getTournamentEditValidationSchema();

export const planPricingValidationSchema = Yup.object().shape({
  country: Yup.string()
    .required("Country is required")
    .test(
      "no-spaces",
      "Country should not contain only spaces",
      (value) => !/^\s+$/.test(value)
    ),
  currency: Yup.string().required("Currency is required"),
  monthly_price: Yup.string()
    .required("Monthly Price is required")
    .matches(/^\d+(\.\d+)?$/, "Monthly Price must be a valid number")
    .test(
      "no-spaces",
      "Monthly Price should not contain only spaces",
      (value) => !/^\s+$/.test(value)
    ),
  yearly_price: Yup.string()
    .required("Yearly Price is required")
    .matches(/^\d+(\.\d+)?$/, "Yearly Price must be a valid number")
    .test(
      "no-spaces",
      "Yearly Price should not contain only spaces",
      (value) => !/^\s+$/.test(value)
    ),
});